/**
 * This file provides utility functions for generating consistent
 * and visually appealing topic tag colors that match our application theme.
 */

// Base color palette that matches our application theme - using more muted, elegant colors
const THEME_COLORS = {
    // Blues (primary theme colors)
    blue: {
        light: "bg-blue-50 text-blue-700",
        medium: "bg-blue-100 text-blue-700",
        dark: "bg-blue-200 text-blue-800",
    },
    // Teals
    teal: {
        light: "bg-teal-50 text-teal-700",
        medium: "bg-teal-100 text-teal-700",
        dark: "bg-teal-200 text-teal-800",
    },
    // Indigos
    indigo: {
        light: "bg-indigo-50 text-indigo-700",
        medium: "bg-indigo-100 text-indigo-700",
        dark: "bg-indigo-200 text-indigo-800",
    },
    // Violets
    violet: {
        light: "bg-violet-50 text-violet-700",
        medium: "bg-violet-100 text-violet-700",
        dark: "bg-violet-200 text-violet-800",
    },
    // Purples
    purple: {
        light: "bg-purple-50 text-purple-700",
        medium: "bg-purple-100 text-purple-700",
        dark: "bg-purple-200 text-purple-800",
    },
    // Cyans
    cyan: {
        light: "bg-cyan-50 text-cyan-700",
        medium: "bg-cyan-100 text-cyan-700",
        dark: "bg-cyan-200 text-cyan-800",
    },
    // Sky
    sky: {
        light: "bg-sky-50 text-sky-700",
        medium: "bg-sky-100 text-sky-700",
        dark: "bg-sky-200 text-sky-800",
    },
    // Emeralds (green tones)
    emerald: {
        light: "bg-emerald-50 text-emerald-700",
        medium: "bg-emerald-100 text-emerald-700",
        dark: "bg-emerald-200 text-emerald-800",
    },
    // Ambers (warm accent) - softened
    amber: {
        light: "bg-amber-50 text-amber-700",
        medium: "bg-amber-100 text-amber-700",
        dark: "bg-amber-200 text-amber-800",
    },
    // Roses (for strategic accents) - softened
    rose: {
        light: "bg-rose-50 text-rose-700",
        medium: "bg-rose-100 text-rose-700",
        dark: "bg-rose-200 text-rose-800",
    },
    // Slate (neutral tones)
    slate: {
        light: "bg-slate-100 text-slate-700",
        medium: "bg-slate-200 text-slate-700",
        dark: "bg-slate-300 text-slate-800",
    },
};

// Flatten the color palette for easier access
const COLORS_LIST = Object.entries(THEME_COLORS).flatMap(([_, variants]) =>
    Object.values(variants)
);

// Map specific topics to specific colors for consistency
const TOPIC_COLOR_MAP: Record<string, string> = {
    // Strategy topics
    Strategy: THEME_COLORS.blue.medium,
    "Market Entry": THEME_COLORS.indigo.medium,
    "Growth Strategy": THEME_COLORS.sky.medium,
    "Digital Strategy": THEME_COLORS.cyan.medium,

    // Financial topics
    Profitability: THEME_COLORS.emerald.medium,
    Pricing: THEME_COLORS.teal.medium,
    "Cost Reduction": THEME_COLORS.emerald.dark,
    "Financial Analysis": THEME_COLORS.teal.dark,

    // Market topics
    "Market Sizing": THEME_COLORS.violet.medium,
    "Competitive Analysis": THEME_COLORS.purple.medium,
    "Market Research": THEME_COLORS.violet.light,

    // Operations topics
    Operations: THEME_COLORS.amber.medium,
    "Supply Chain": THEME_COLORS.amber.dark,
    "Process Improvement": THEME_COLORS.amber.light,

    // Organization topics
    Organization: THEME_COLORS.rose.medium,
    HR: THEME_COLORS.rose.light,
    "Change Management": THEME_COLORS.rose.dark,

    // Technology topics
    Technology: THEME_COLORS.blue.dark,
    Innovation: THEME_COLORS.indigo.dark,
    "Digital Transformation": THEME_COLORS.sky.dark,

    // Generic/Other topics
    Other: THEME_COLORS.slate.medium,
};

/**
 * Get a consistent color for a topic based on its name.
 * If the topic has a predefined color, it will use that.
 * Otherwise, it will deterministically assign a color based on the topic name.
 *
 * @param topicName The name of the topic
 * @returns A Tailwind CSS class string for background and text color
 */
export const getTopicColor = (topicName: string): string => {
    // If this topic has a predefined color, use it
    if (TOPIC_COLOR_MAP[topicName]) {
        return TOPIC_COLOR_MAP[topicName];
    }

    // Otherwise, assign a color based on the first character of the topic name
    // This ensures the same topic always gets the same color
    const charCode = topicName.charCodeAt(0);
    const colorIndex = charCode % COLORS_LIST.length;

    return COLORS_LIST[colorIndex];
};

/**
 * Utility function to get full topic styling
 *
 * @param topicName The name of the topic
 * @returns A Tailwind CSS class string with all necessary styling
 */
export const getTopicStyle = (topicName: string): string => {
    return `px-3 py-1 text-sm font-medium rounded-full ${getTopicColor(
        topicName
    )}`;
};
